import { useState, useEffect, useCallback, useRef } from 'react';
import { webhookService } from '../services/webhook';
import {
  WebhookSummary,
  WebhookPaginatedResponse,
  WebhookUnifiedEvent,
  WebhookStripeEvent,
  WebhookStripeParams,
  WebhookCheckrEvent,
  WebhookCheckrParams,
  WebhookBookingEvent,
  WebhookBookingParams,
  WebhookVideoEvent,
  WebhookGroupSessionEvent,
  WebhookSessionMonitoring,
  WebhookSessionParams,
  WebhookSafetyFlag,
  WebhookSafetyParams,
  WebhookReferralEvent,
  WebhookReferralParams,
  WebhookFraudSignal,
  WebhookFraudParams,
  WebhookCallLog,
  WebhookCallLogParams,
  WebhookAnalyticsEvent,
  WebhookAnalyticsParams,
  WebhookAuditLog,
  WebhookAuditParams,
  WebhookUploadEvent,
  WebhookUploadParams,
  WebhookBaseParams,
} from '../types/webhook';

// ============================================================================
// useWebhookSummary
// ============================================================================

interface UseSummaryReturn {
  data: WebhookSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useWebhookSummary(): UseSummaryReturn {
  const [state, setState] = useState<{ data: WebhookSummary | null; loading: boolean; error: string | null }>({
    data: null,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await webhookService.getSummary();
      setState({ data: response.data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch webhook summary';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// Generic paginated hook factory
// ============================================================================

interface PaginatedState<T> {
  items: T[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
}

interface PaginatedReturn<T> extends PaginatedState<T> {
  refetch: () => Promise<void>;
}

function usePaginatedWebhook<T, P extends WebhookBaseParams>(
  fetcher: (params: P) => Promise<WebhookPaginatedResponse<T>>,
  params: P,
  deps: unknown[],
  errorLabel: string,
): PaginatedReturn<T> {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<PaginatedState<T>>({
    items: [],
    total: 0,
    loading: true,
    error: null,
    page: Math.floor(offset / limit) + 1,
    totalPages: 0,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetcher(paramsRef.current);
      const { items, total, limit: rl, offset: ro } = response.data;
      setState({
        items,
        total,
        loading: false,
        error: null,
        page: Math.floor(ro / rl) + 1,
        totalPages: Math.ceil(total / rl),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to fetch ${errorLabel}`;
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetch(); }, [fetch, ...deps]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// Individual hooks
// ============================================================================

export function useWebhookEvents(params: WebhookBaseParams): PaginatedReturn<WebhookUnifiedEvent> {
  return usePaginatedWebhook(
    webhookService.getUnifiedEvents, params,
    [params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'events',
  );
}

export function useWebhookStripe(params: WebhookStripeParams): PaginatedReturn<WebhookStripeEvent> {
  return usePaginatedWebhook(
    webhookService.getStripeEvents, params,
    [params.eventType, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'Stripe events',
  );
}

export function useWebhookCheckr(params: WebhookCheckrParams): PaginatedReturn<WebhookCheckrEvent> {
  return usePaginatedWebhook(
    webhookService.getCheckrEvents, params,
    [params.eventType, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'Checkr events',
  );
}

export function useWebhookBookings(params: WebhookBookingParams): PaginatedReturn<WebhookBookingEvent> {
  return usePaginatedWebhook(
    webhookService.getBookingEvents, params,
    [params.eventType, params.bookingId, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'booking events',
  );
}

export function useWebhookVideoEvents(params: WebhookSessionParams): PaginatedReturn<WebhookVideoEvent> {
  return usePaginatedWebhook(
    webhookService.getVideoEvents, params,
    [params.eventType, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'video events',
  );
}

export function useWebhookGroupSessions(params: WebhookSessionParams): PaginatedReturn<WebhookGroupSessionEvent> {
  return usePaginatedWebhook(
    webhookService.getGroupSessionEvents, params,
    [params.eventType, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'group session events',
  );
}

export function useWebhookSessionMonitoring(params: WebhookSessionParams): PaginatedReturn<WebhookSessionMonitoring> {
  return usePaginatedWebhook(
    webhookService.getSessionMonitoring, params,
    [params.eventType, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'session monitoring',
  );
}

export function useWebhookSafetyFlags(params: WebhookSafetyParams): PaginatedReturn<WebhookSafetyFlag> {
  return usePaginatedWebhook(
    webhookService.getSafetyFlags, params,
    [params.severity, params.flagType, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'safety flags',
  );
}

export function useWebhookReferrals(params: WebhookReferralParams): PaginatedReturn<WebhookReferralEvent> {
  return usePaginatedWebhook(
    webhookService.getReferralEvents, params,
    [params.eventType, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'referral events',
  );
}

export function useWebhookFraudSignals(params: WebhookFraudParams): PaginatedReturn<WebhookFraudSignal> {
  return usePaginatedWebhook(
    webhookService.getFraudSignals, params,
    [params.severity, params.signalType, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'fraud signals',
  );
}

export function useWebhookCallLogs(params: WebhookCallLogParams): PaginatedReturn<WebhookCallLog> {
  return usePaginatedWebhook(
    webhookService.getCallLogs, params,
    [params.type, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'call logs',
  );
}

export function useWebhookAnalytics(params: WebhookAnalyticsParams): PaginatedReturn<WebhookAnalyticsEvent> {
  return usePaginatedWebhook(
    webhookService.getAnalyticsEvents, params,
    [params.category, params.eventName, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'analytics events',
  );
}

export function useWebhookAuditLogs(params: WebhookAuditParams): PaginatedReturn<WebhookAuditLog> {
  return usePaginatedWebhook(
    webhookService.getAuditLogs, params,
    [params.category, params.severity, params.action, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'audit logs',
  );
}

export function useWebhookUploads(params: WebhookUploadParams): PaginatedReturn<WebhookUploadEvent> {
  return usePaginatedWebhook(
    webhookService.getUploadEvents, params,
    [params.eventType, params.status, params.search, params.startDate, params.endDate, params.offset, params.limit],
    'upload events',
  );
}
